import {
  signInWithPopup,
  signInWithRedirect,
  type AuthProvider,
} from "firebase/auth";
import { auth } from "./firebase";
import { showToast, formatError } from "./dom";

export interface ValidatedCredentials {
  email: string;
  password: string;
}

interface ValidateOptions {
  requirePassword?: boolean;
}

function normalizeEmail(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

function isAppleTouchDevice(): boolean {
  const platform = navigator.platform || "";
  const userAgent = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    ((platform === "MacIntel" || /Macintosh/.test(userAgent)) &&
      navigator.maxTouchPoints > 1)
  );
}

function shouldPreferRedirectForProvider(): boolean {
  return isAppleTouchDevice();
}

export function validateCredentials(
  emailInput: HTMLInputElement,
  passwordInput: HTMLInputElement,
  options: ValidateOptions = {},
): ValidatedCredentials | null {
  const { requirePassword = true } = options;
  const email = normalizeEmail(emailInput.value);
  const password = String(passwordInput.value || "");

  if (!email) {
    showToast("Enter your email address first.", true);
    emailInput.focus();
    return null;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    showToast("Enter a valid email address.", true);
    emailInput.focus();
    return null;
  }

  if (requirePassword && password.length < 6) {
    showToast("Password must be at least 6 characters.", true);
    passwordInput.focus();
    return null;
  }

  return { email, password };
}

export function shouldUseRedirect(): boolean {
  return shouldPreferRedirectForProvider() || /Android/i.test(navigator.userAgent);
}

export async function signInWithProvider(
  provider: AuthProvider,
  label: string,
): Promise<void> {
  try {
    if (shouldPreferRedirectForProvider()) {
      await signInWithRedirect(auth, provider);
      return;
    }

    await signInWithPopup(auth, provider);
    showToast(`Signed in with ${label}.`);
  } catch (error: unknown) {
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? String((error as { code?: unknown }).code || "")
        : "";

    if (
      code === "auth/popup-blocked" ||
      code === "auth/operation-not-supported-in-this-environment"
    ) {
      try {
        await signInWithRedirect(auth, provider);
        return;
      } catch (redirectError: unknown) {
        console.warn(`${label} redirect sign-in failed:`, redirectError);
        showToast(formatError(redirectError), true);
        return;
      }
    }

    console.warn(`${label} sign-in failed:`, error);
    showToast(formatError(error), true);
  }
}
